"use client"

import { Card } from "@/components/UI/card"
import { Button } from "@/components/UI/button"
import { Badge } from "@/components/UI/badge"
import { Link2, Trash2, Edit, Copy, Calendar, Layers, FolderTree, ImageIcon, User, ExternalLink } from "lucide-react"

interface SubLink {
  id: number;
  url: string;
  inherit_mode: "include" | "exclude";
  created_by: number;
  created_at: string;
  updated_by: number;
  updated_at: string;
  meta: {
    home_view: string | null;
    title: string;
    description: string | null;
    image: string | null;
  };
  entity_counts: {
    asset?: number;
    group?: number;
    scene?: number;
  };
  total_entities: number;
}

interface LinkListProps {
  links: SubLink[]
  onDelete: (id: number) => void
  onEdit: (id: number) => void
}

export function LinkList({ links, onDelete, onEdit }: LinkListProps) {
  const copyToClipboard = (linkId: number) => {
    const url = `${window.location.origin}${links.find(l => l.id === linkId)?.url || ''}`
    navigator.clipboard.writeText(url)
  }

  const handleOpenSubLink = (linkUrl: string) => {
    const baseUrl = window.location.href.split('/admin')[0]
    window.open(`${baseUrl}/${linkUrl}`, '_blank')
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('vi-VN')
  }

  if (links.length === 0) {
    return (
      <Card className="p-12 bg-card border-border text-center">
        <div className="flex flex-col items-center gap-4">
          <div className="p-4 rounded-full bg-secondary/50">
            <Link2 className="w-8 h-8 text-muted-foreground" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-1">Chưa có link nào</h3>
            <p className="text-sm text-muted-foreground">Tạo link con đầu tiên của bạn để bắt đầu</p>
          </div>
        </div>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {links.map((link) => (
        <Card key={link.id} className="p-6 bg-card border-border hover:border-primary/50 transition-colors">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 space-y-4">
              <div className="flex items-start gap-4">
                <div className="p-2.5 rounded-lg bg-primary/10 mt-1">
                  <Link2 className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-foreground">{link.meta.title}</h3>
                    <Badge
                      variant={link.inherit_mode === "include" ? "default" : "secondary"}
                      className={
                        link.inherit_mode === "include"
                          ? "bg-primary/20 text-primary border-primary/30"
                          : "bg-secondary text-muted-foreground"
                      }
                    >
                      {link.inherit_mode === "include" ? "Bao gồm" : "Loại trừ"}
                    </Badge>
                  </div>

                  {/* URL */}
                  <div className="flex items-center gap-2 mb-2">
                    <code className="text-xs bg-secondary/50 px-2 py-1 rounded text-muted-foreground">
                      {link.url}
                    </code>
                  </div>

                  {link.meta.description && (
                    <p className="text-sm text-muted-foreground mb-3">{link.meta.description}</p>
                  )}

                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5" />
                      <span>{formatDate(link.created_at)}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <User className="w-3.5 h-3.5" />
                      <span>User {link.created_by}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Layers className="w-3.5 h-3.5" />
                      <span>{link.total_entities} entities</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Entity counts breakdown */}
              {link.total_entities > 0 && (
                <div className="ml-14 space-y-2">
                  <div className="flex flex-wrap gap-1.5">
                    {link.entity_counts.scene && (
                      <Badge variant="outline" className="text-xs bg-blue-50 border-blue-200 text-blue-700">
                        <Layers className="w-3 h-3 mr-1" />
                        {link.entity_counts.scene} scenes
                      </Badge>
                    )}
                    {link.entity_counts.group && (
                      <Badge variant="outline" className="text-xs bg-green-50 border-green-200 text-green-700">
                        <FolderTree className="w-3 h-3 mr-1" />
                        {link.entity_counts.group} groups
                      </Badge>
                    )}
                    {link.entity_counts.asset && (
                      <Badge variant="outline" className="text-xs bg-purple-50 border-purple-200 text-purple-700">
                        <ImageIcon className="w-3 h-3 mr-1" />
                        {link.entity_counts.asset} assets
                      </Badge>
                    )}
                  </div>

                  {/* Image preview if available */}
                  {link.meta.image && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Preview:</span>
                      <img
                        src={link.meta.image}
                        alt={link.meta.title}
                        className="w-16 h-12 object-cover rounded border"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none'
                        }}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleOpenSubLink(link.url)}
                className="text-muted-foreground hover:text-foreground"
                title="Mở trong tab mới"
              >
                <ExternalLink className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => copyToClipboard(link.id)}
                className="text-muted-foreground hover:text-foreground"
              >
                <Copy className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onEdit(link.id)}
                className="text-muted-foreground hover:text-foreground"
              >
                <Edit className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onDelete(link.id)}
                className="text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  )
}
